<div class="cyber-scan-header-spectacular">
  <div class="cyber-header-icon">
    <i class="bi bi-gear-fill text-cyber-accent"></i>
  </div>
  <div>
    <h2 class="h3 mb-1">Scanning Configuration</h2>
    <p class="text-muted mb-0">Configure your technical scanning preferences</p>
  </div>
</div>

<div class="cyber-filters-spectacular">
  <div class="cyber-card">
    <div class="row">
      <div class="col-12">
        <!-- Tier Badge -->
        <div class="d-flex align-items-center mb-4">
          <span [class]="'badge me-3 ' + getTierBadgeClass()">
            <i class="bi" [class]="getTierIcon()"></i>
            {{ tierInfo.tierName | titlecase }}
          </span>
          <div class="tier-description">
            <p class="text-muted mb-0">{{ tierInfo.description }}</p>
          </div>
        </div>

        <!-- Progress Indicator -->
        <div class="progress-indicator mb-4">
          <div class="progress">
            <div
              class="progress-bar bg-cyber-accent"
              role="progressbar"
              [style.width]="'75%'"
              aria-label="Registration progress: Step 3 of 4 complete"
              >
              <span class="visually-hidden">75% Complete</span>
            </div>
          </div>
          <small class="text-muted">Step 3 of 4</small>
        </div>

        <!-- Configuration Form -->
        <form [formGroup]="scanningConfigForm" (ngSubmit)="onSubmit()">
          <!-- Network Configuration Section -->
          <div class="network-config-section mb-4">
            <h4 class="cyber-section-title">
              <i class="bi bi-diagram-3-fill cyber-section-icon"></i>
              Network Configuration
            </h4>

            <div class="row">
              <div class="col-md-6 mb-3">
                <label for="networkType" class="cyber-label">
                  <i class="bi bi-router-fill cyber-label-icon"></i>
                  Network Type *
                </label>
                <select title="Form select"
                  class="form-select"
                  id="networkType"
                  formControlName="networkType"
                  [class.is-invalid]="
                    scanningConfigForm.get('networkType')?.invalid &&
                    scanningConfigForm.get('networkType')?.touched
                  "
                  >
                  <option value="">Select network type...</option>
                  <option value="internal">Internal Network</option>
                  <option value="external">External/Public Network</option>
                  <option value="hybrid">Hybrid (Internal & External)</option>
                  <option value="cloud">Cloud Infrastructure</option>
                </select>
                @if (
                  scanningConfigForm.get('networkType')?.invalid &&
                  scanningConfigForm.get('networkType')?.touched
                  ) {
                  <div
                    class="invalid-feedback"
                    >
                    Network type is required
                  </div>
                }
              </div>

              <div class="col-md-6 mb-3">
                <label for="scanFrequency" class="cyber-label">
                  <i class="bi bi-clock-fill cyber-label-icon"></i>
                  Preferred Scan Frequency *
                </label>
                <select title="Form select"
                  class="form-select"
                  id="scanFrequency"
                  formControlName="scanFrequency"
                  [class.is-invalid]="
                    scanningConfigForm.get('scanFrequency')?.invalid &&
                    scanningConfigForm.get('scanFrequency')?.touched
                  "
                  >
                  <option value="">Select frequency...</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="on-demand">On-Demand Only</option>
                </select>
                @if (
                  scanningConfigForm.get('scanFrequency')?.invalid &&
                  scanningConfigForm.get('scanFrequency')?.touched
                  ) {
                  <div
                    class="invalid-feedback"
                    >
                    Scan frequency is required
                  </div>
                }
              </div>
            </div>
          </div>

          <!-- Alert Configuration Section -->
          <div class="alert-config-section mb-4">
            <h4 class="cyber-section-title">
              <i class="bi bi-bell-fill cyber-section-icon"></i>
              Alert Configuration
            </h4>

            <div class="row">
              <div class="col-md-6 mb-3">
                <label for="alertLevel" class="cyber-label">
                  <i
                    class="bi bi-exclamation-triangle-fill cyber-label-icon"
                  ></i>
                  Alert Threshold *
                </label>
                <select title="Form select"
                  class="form-select"
                  id="alertLevel"
                  formControlName="alertLevel"
                  [class.is-invalid]="
                    scanningConfigForm.get('alertLevel')?.invalid &&
                    scanningConfigForm.get('alertLevel')?.touched
                  "
                  >
                  <option value="">Select alert level...</option>
                  <option value="critical">Critical Only</option>
                  <option value="high">High & Critical</option>
                  <option value="medium">Medium & Above</option>
                  <option value="all">All Vulnerabilities</option>
                </select>
                @if (
                  scanningConfigForm.get('alertLevel')?.invalid &&
                  scanningConfigForm.get('alertLevel')?.touched
                  ) {
                  <div
                    class="invalid-feedback"
                    >
                    Alert threshold is required
                  </div>
                }
              </div>

              <div class="col-md-6 mb-3">
                <label for="notificationMethod" class="cyber-label">
                  <i class="bi bi-envelope-fill cyber-label-icon"></i>
                  Notification Method *
                </label>
                <select title="Form select"
                  class="form-select"
                  id="notificationMethod"
                  formControlName="notificationMethod"
                  [class.is-invalid]="
                    scanningConfigForm.get('notificationMethod')?.invalid &&
                    scanningConfigForm.get('notificationMethod')?.touched
                  "
                  >
                  <option value="">Select method...</option>
                  <option value="email">Email</option>
                  <option value="sms">SMS</option>
                  <option value="dashboard">Dashboard Only</option>
                  <option value="webhook">Webhook</option>
                  <option value="teams">Microsoft Teams</option>
                  <option value="slack">Slack</option>
                </select>
                @if (
                  scanningConfigForm.get('notificationMethod')?.invalid &&
                  scanningConfigForm.get('notificationMethod')?.touched
                  ) {
                  <div
                    class="invalid-feedback"
                    >
                    Notification method is required
                  </div>
                }
              </div>
            </div>
          </div>

          <!-- Integration Preferences Section -->
          <div class="integration-config-section mb-4">
            <h4 class="cyber-section-title">
              <i class="bi bi-link-45deg cyber-section-icon"></i>
              Integration Preferences
            </h4>

            <div class="row">
              <div class="col-md-6 mb-3">
                <label for="integrationType" class="cyber-label">
                  <i class="bi bi-puzzle-fill cyber-label-icon"></i>
                  Primary Integration
                </label>
                <select title="Form select"
                  class="form-select"
                  id="integrationType"
                  formControlName="integrationType"
                  >
                  <option value="">No integration needed</option>
                  <option value="siem">SIEM Platform</option>
                  <option value="ticketing">Ticketing System</option>
                  <option value="api">Custom API</option>
                  <option value="reporting">Reporting Tool</option>
                </select>
              </div>

              <div class="col-md-6 mb-3">
                <label for="complianceRequirements" class="cyber-label">
                  <i class="bi bi-shield-check cyber-label-icon"></i>
                  Compliance Requirements
                </label>
                <select title="Form select"
                  class="form-select"
                  id="complianceRequirements"
                  formControlName="complianceRequirements"
                  >
                  <option value="">Select if applicable...</option>
                  <option value="pci-dss">PCI DSS</option>
                  <option value="hipaa">HIPAA</option>
                  <option value="sox">SOX</option>
                  <option value="gdpr">GDPR</option>
                  <option value="iso27001">ISO 27001</option>
                  <option value="nist">NIST Framework</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
          </div>

          <!-- Additional Requirements Section -->
          <div class="additional-requirements-section mb-4">
            <h4 class="cyber-section-title">
              <i class="bi bi-list-check cyber-section-icon"></i>
              Additional Requirements
            </h4>

            <div class="mb-3">
              <label for="additionalRequirements" class="cyber-label">
                <i class="bi bi-chat-text cyber-label-icon"></i>
                Special Requirements or Notes
              </label>
              <textarea title="Form text area"
                class="form-control"
                id="additionalRequirements"
                rows="3"
                formControlName="additionalRequirements"
                placeholder="Describe any specific requirements, constraints, or goals for your security scanning program..."
              ></textarea>
              <div class="form-text text-muted">
                Help us tailor your evaluation experience by sharing specific
                needs or objectives.
              </div>
            </div>
          </div>

          <!-- Tier-Specific Help Text -->
          @if (isTrialOrPremium) {
            <div
              class="cyber-alert cyber-alert-info mb-4"
              >
              <div class="d-flex">
                <i class="bi bi-info-circle-fill flex-shrink-0 me-2"></i>
                <div>
                  <h6 class="cyber-alert-heading">Configuration Benefits</h6>
                  <p class="mb-0">
                    These preferences help us set up your
                    {{ tierInfo.tierName }} evaluation with optimal settings for
                    your environment and requirements.
                  </p>
                </div>
              </div>
            </div>
          }

          <!-- Error Display -->
          @if (errorMessage) {
            <div class="cyber-alert cyber-alert-danger mb-3">
              <div class="d-flex">
                <i class="bi bi-exclamation-triangle-fill flex-shrink-0 me-2"></i>
                <div>
                  <h6 class="cyber-alert-heading">Configuration Error</h6>
                  <p class="mb-0">{{ errorMessage }}</p>
                </div>
              </div>
            </div>
          }

          <!-- Form Actions -->
          <div class="d-flex justify-content-between align-items-center">
            <button
              type="button"
              class="btn btn-outline-secondary"
              (click)="goBack()"
              >
              <i class="bi bi-arrow-left me-2"></i>
              Back to Company Setup
            </button>

            <div class="d-flex gap-2">
              <button
                type="button"
                class="btn btn-outline-primary"
                (click)="saveAsDraft()"
                >
                <i class="bi bi-save me-2"></i>
                Save Draft
              </button>
              <button
                type="submit"
                class="btn btn-cyber-primary"
                [disabled]="scanningConfigForm.invalid || isLoading"
                >
                @if (isLoading) {
                  <span
                    class="spinner-border spinner-border-sm me-2"
                  ></span>
                }
                @if (!isLoading) {
                  <i class="bi bi-arrow-right me-2"></i>
                }
                Continue to Summary
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</div>
